/**
 * Analytics Tracking Hook - Tracks user actions to:
 * 1. Backend MongoDB (Admin Panel)
 * 2. Google Analytics 4 (GA4)
 */
import { useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ GA4 HELPER ============
/**
 * Send event to Google Analytics 4
 */
function sendToGA4(eventName, params = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...params,
      send_to: 'G-10MML7KRG0'
    });
    console.log(`[GA4] Event: ${eventName}`, params);
  }
}

// ============ SESSION MANAGEMENT ============
/**
 * Get or create session ID
 */
function getSessionId() {
  let sessionId = localStorage.getItem("upnext_session");
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("upnext_session", sessionId);
  }
  return sessionId;
}

/**
 * Get device type
 */
function getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

// ============ PAGE VIEW TRACKING ============
/**
 * Track page view on mount with session tracking
 * Sends to: Backend + GA4
 */
export function usePageView(pageName) {
  const tracked = useRef(false);
  
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    
    const sessionId = getSessionId();
    const referrer = document.referrer || null;
    const deviceType = getDeviceType();
    
    // Get UTM params from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    
    // 1. Track to Backend
    axios.post(`${API}/analytics/pageview`, null, {
      params: { 
        page: pageName, 
        session_id: sessionId, 
        referrer,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign
      }
    }).catch(() => {});
    
    // 2. Track to GA4
    sendToGA4('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pageName,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      device_type: deviceType
    });
    
    // Start session
    axios.post(`${API}/analytics/session/start`, null, {
      params: { session_id: sessionId, page: pageName }
    }).catch(() => {});
    
    // End session on page unload
    const handleUnload = () => {
      navigator.sendBeacon?.(`${API}/analytics/session/end?session_id=${sessionId}`);
    };
    
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [pageName]);
}

// ============ EVENT VIEW TRACKING ============
/**
 * Track event view
 * Sends to: Backend + GA4
 */
export function trackEventView(eventId, eventTitle = '', eventCategory = '') {
  const sessionId = getSessionId();
  
  // 1. Track to Backend
  axios.post(`${API}/analytics/view`, null, {
    params: { event_id: eventId, session_id: sessionId }
  }).catch(() => {});
  
  // 2. Track to GA4
  sendToGA4('view_event', {
    event_id: eventId,
    event_title: eventTitle,
    event_category: eventCategory,
    content_type: 'event'
  });
}

// ============ BOOKMARK TRACKING ============
/**
 * Track bookmark action
 * Sends to: Backend + GA4
 */
export function trackBookmark(eventId, action, eventTitle = '') {
  const sessionId = getSessionId();
  
  // 1. Track to Backend
  const promise = axios.post(`${API}/analytics/bookmark`, null, {
    params: { event_id: eventId, session_id: sessionId, action }
  }).catch(() => {});
  
  // 2. Track to GA4
  sendToGA4('bookmark_event', {
    event_id: eventId,
    event_title: eventTitle,
    action: action, // 'add' or 'remove'
    method: 'click'
  });
  
  // 3. GA4 Conversion (only for add)
  if (action === 'add') {
    sendToGA4('conversion', {
      event_name: 'bookmark_add',
      event_id: eventId
    });
  }
  
  return promise;
}

// ============ SHARE TRACKING ============
/**
 * Track share action
 * Sends to: Backend + GA4
 */
export function trackShare(platform, eventId = null, listType = null, eventTitle = '') {
  const sessionId = getSessionId();
  
  // 1. Track to Backend
  const promise = axios.post(`${API}/analytics/share`, null, {
    params: { platform, event_id: eventId, list_type: listType, session_id: sessionId }
  }).catch(() => {});
  
  // 2. Track to GA4 - Standard share event
  sendToGA4('share', {
    method: platform,
    content_type: eventId ? 'event' : 'list',
    item_id: eventId || listType,
    content_id: eventId
  });
  
  // 3. Track platform-specific for detailed analysis
  sendToGA4(`share_${platform}`, {
    event_id: eventId,
    event_title: eventTitle,
    list_type: listType
  });
  
  return promise;
}

// ============ GOING/INTERESTED TRACKING ============
/**
 * Track "I'm Going" action
 * Sends to: Backend + GA4
 */
export function trackGoing(eventId, eventTitle = '', action = 'going') {
  const sessionId = getSessionId();
  
  // 1. Track to Backend
  axios.post(`${API}/events/${eventId}/going`, null, {
    params: { session_id: sessionId }
  }).catch(() => {});
  
  // 2. Track to GA4
  sendToGA4('event_rsvp', {
    event_id: eventId,
    event_title: eventTitle,
    rsvp_type: action, // 'going' or 'interested'
    method: 'click'
  });
  
  // 3. GA4 Conversion
  sendToGA4('conversion', {
    event_name: 'event_going',
    event_id: eventId
  });
}

// ============ NEWSLETTER TRACKING ============
/**
 * Track newsletter signup
 * Sends to: Backend + GA4
 */
export function trackNewsletterSignup(email, source = 'homepage') {
  // Track to GA4
  sendToGA4('newsletter_signup', {
    method: source,
    success: true
  });
  
  // GA4 Conversion
  sendToGA4('conversion', {
    event_name: 'newsletter_signup'
  });
  
  // GA4 Generate Lead (standard event)
  sendToGA4('generate_lead', {
    currency: 'MYR',
    value: 1
  });
}

// ============ SEARCH TRACKING ============
/**
 * Track search
 * Sends to: Backend + GA4
 */
export function trackSearch(query, resultsCount) {
  // 1. Track to Backend
  axios.post(`${API}/analytics/search`, null, {
    params: { query, results_count: resultsCount }
  }).catch(() => {});
  
  // 2. Track to GA4 - Standard search event
  sendToGA4('search', {
    search_term: query,
    results_count: resultsCount
  });
}

// ============ FILTER TRACKING ============
/**
 * Track filter usage
 * Sends to: GA4 only
 */
export function trackFilter(filterType, filterValue) {
  sendToGA4('filter_use', {
    filter_type: filterType, // 'category', 'state', 'month', 'family_friendly'
    filter_value: filterValue
  });
}

// ============ CLICK TRACKING ============
/**
 * Track important clicks
 * Sends to: GA4 only
 */
export function trackClick(elementName, elementType = 'button', destination = null) {
  sendToGA4('click', {
    element_name: elementName,
    element_type: elementType,
    link_url: destination
  });
}

// ============ QR CODE TRACKING ============
/**
 * Track QR code generation/scan
 * Sends to: GA4 only
 */
export function trackQRCode(eventId, eventTitle, action = 'generate') {
  sendToGA4('qr_code', {
    event_id: eventId,
    event_title: eventTitle,
    action: action // 'generate', 'download', 'share'
  });
}

// ============ ACTIVITY FEED ============
/**
 * Log activity for feed
 */
export function logActivity(action, eventId, eventTitle, userName = "Someone") {
  axios.post(`${API}/analytics/activity/log`, null, {
    params: { action, event_id: eventId, event_title: eventTitle, user_name: userName }
  }).catch(() => {});
}

// ============ CUSTOM HOOK ============
/**
 * Custom hook for tracking with all methods
 */
export function useAnalytics() {
  const sessionId = getSessionId();
  
  const trackPage = useCallback((page) => {
    axios.post(`${API}/analytics/pageview`, null, {
      params: { page, session_id: sessionId, referrer: document.referrer }
    }).catch(() => {});
    
    sendToGA4('page_view', {
      page_path: page,
      page_location: window.location.href
    });
  }, [sessionId]);
  
  const trackEvent = useCallback((eventId, eventTitle = '') => {
    trackEventView(eventId, eventTitle);
  }, []);
  
  const trackBookmarkAction = useCallback((eventId, action, eventTitle = '') => {
    return trackBookmark(eventId, action, eventTitle);
  }, []);
  
  const trackShareAction = useCallback((platform, eventId, listType, eventTitle = '') => {
    return trackShare(platform, eventId, listType, eventTitle);
  }, []);
  
  return {
    sessionId,
    trackPage,
    trackEvent,
    trackBookmark: trackBookmarkAction,
    trackShare: trackShareAction,
    trackSearch,
    trackGoing,
    trackNewsletterSignup,
    trackFilter,
    trackClick,
    trackQRCode,
    logActivity,
    sendToGA4 // Direct access if needed
  };
}

export default useAnalytics;
